import { IPaymentSetupData, ISubscriptionPaymentSummary } from 'fitbeat.models';
import React, { FunctionComponent } from 'react';
import {
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import Button, { ButtonTypes } from '../../components/Button';
import Theme from '../../theme';
import { defaultScale } from '../../utils';
import { InfoAlert } from './InfoAlert';
import AddCoupon from './ManageCoupon';
import { OrderSummaryNote } from './OrderSummaryNote';
import SpecialOfferSubscriptionSummary from './SpecialOfferSubscriptionSummary';
import { StripePaymentElementProvider } from './StripePaymentElementProvider';
import SubscriptionSummary from './SubscriptionSummary';
import TrialSubscriptionSummary from './TrialSubscriptionSummary';

interface IProps {
  paymentSubscriptionSummary: undefined | ISubscriptionPaymentSummary;
  total: number;
  allowAddRemoveCoupon: boolean;
  userId: string;
  paymentSetupData: IPaymentSetupData;
  seeMembershipPlans: () => void;
  shouldGetCardDetails: boolean;
  shouldShowMembershipPlansButton: boolean;
  shouldShowContinueButton: boolean;
  paymentMethodAdded: () => void;
  stripeCustomerId: string;
  billingStartDate?: string;
  t: (key: string, options?: any) => string;
}

export const PaymentSummaryScreenContent: FunctionComponent<IProps> = (
  {
    paymentSubscriptionSummary,
    total,
    allowAddRemoveCoupon,
    userId,
    paymentSetupData,
    seeMembershipPlans,
    paymentMethodAdded,
    shouldGetCardDetails,
    shouldShowContinueButton,
    stripeCustomerId,
    billingStartDate,
    shouldShowMembershipPlansButton,
    t,
  },
) => {

  const getScreenContent = () => {
    if (!paymentSubscriptionSummary) {
      return <></>;
    }

    if (billingStartDate) {
      return <SpecialOfferSubscriptionSummary
        t={t}
        total={total}
        billingStartDate={billingStartDate}
        paymentSubscriptionSummary={paymentSubscriptionSummary}
      />;
    }

    if (paymentSubscriptionSummary.isTrialPeriodActive) {
      return <TrialSubscriptionSummary
        t={t}
        total={total}
        paymentSubscriptionSummary={paymentSubscriptionSummary}
      />;
    } else {
      return <SubscriptionSummary
        t={t}
        total={total}
        paymentSubscriptionSummary={paymentSubscriptionSummary}
      />;
    }
  };

  return (
    <ScrollView keyboardShouldPersistTaps='handled'>
      <View style={styles.container}>
        {paymentSubscriptionSummary &&
          <>
            <View style={styles.subscriptionSummaryAndCouponContainer}>
              {getScreenContent()}
              {allowAddRemoveCoupon &&
                <AddCoupon userId={userId} />
              }
            </View>

            <OrderSummaryNote paymentSubscriptionSummary={paymentSubscriptionSummary} />

            {shouldShowContinueButton &&
              <Button
                fontColor={Theme.WHITE}
                title={'Continue'}
                onPress={paymentMethodAdded}
              />
            }
            {shouldGetCardDetails &&
              <StripePaymentElementProvider
                t={t}
                paymentSetupData={paymentSetupData}
                stripeCustomerId={stripeCustomerId}
                paymentMethodAdded={paymentMethodAdded}
                userId={userId}
              />
            }
          </>
        }
        {shouldShowMembershipPlansButton &&
          <Button
            fontColor={Theme.PRIMARY}
            buttonType={ButtonTypes.OUTLINE}
            title={t('backToMembershipPlans')}
            onPress={seeMembershipPlans}
            containerStyle={styles.membershipButton}
          />
        }
        <InfoAlert
          message={paymentSubscriptionSummary?.accountStatusChangeValidation?.message}
          messageHeading={paymentSubscriptionSummary?.accountStatusChangeValidation?.messageHeading}
        />
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 5,
    paddingHorizontal: 15,
  },
  subscriptionSummaryAndCouponContainer: {
    paddingBottom: defaultScale(15),
  },
  membershipButton: {
    marginTop: 10,
  },
  activityIndicator: {
    height: '100%',
  },
});
